import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { UserListItem } from '../../../shared/models';
import { UserDetailsResponse } from '../../../shared/models/responses/get-user-details-response.module';

import { ConnectionTypes } from '../../../shared/models/developer-mode/connection-types.model';
import { DeveloperActions } from '../../developer-mode/actions/developer.actions';
import { ModalsActions } from '../../modals/actions/modals.actions';
import { UserApiActions } from '../actions/user-api.actions';
import { updateUserSuccess } from '../actions/user-update-api.action';

export interface UsersState extends EntityState<UserListItem> {
  selectedUserId: string;
  userDetails: UserDetailsResponse | undefined;
  isFetched: boolean;

  monitoredUsers: string[];
  monitoringUsers: string[];
  subAdvisors: string[];

  pendingInvitationEmail: string | null;
}

export const usersAdapter = createEntityAdapter<UserListItem>({
  sortComparer: (a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? ''),
});

const initialState: UsersState = usersAdapter.getInitialState({
  selectedUserId: '',
  userDetails: undefined,
  isFetched: false,

  monitoredUsers: [],
  monitoringUsers: [],
  subAdvisors: [],

  pendingInvitationEmail: null,
});

export const usersReducer = createReducer(
  initialState,
  on(UserApiActions.listUsersSuccess, (state, { userListItem }) =>
    usersAdapter.upsertMany(userListItem, { ...state, isFetched: true })
  ),
  on(UserApiActions.getMonitoredUsersSuccess, (state, { users }) =>
    usersAdapter.upsertMany(users, { ...state })
  ),

  on(UserApiActions.getUserMonitoredUsersSuccess, (state, { users }) =>
    usersAdapter.upsertMany(users, {
      ...state,
      monitoredUsers: users.map((u) => u.id),
    })
  ),

  on(UserApiActions.getMonitoringUsersSuccess, (state, { users }) =>
    usersAdapter.upsertMany(users, {
      ...state,
      monitoringUsers: users.map((u) => u.id),
    })
  ),

  on(UserApiActions.getSubAdvisorsSuccess, (state, { users }) =>
    usersAdapter.upsertMany(users, {
      ...state,
      subAdvisors: users.map((u) => u.id),
    })
  ),

  on(DeveloperActions.connectUsersSuccess, (state, { user, connectionType }) => {
    let updatedState = usersAdapter.upsertOne(user, state);

    switch (connectionType) {
      case ConnectionTypes.doctorToPatient:
        return {
          ...updatedState,
          monitoringUsers: [...new Set([...state.monitoringUsers, user.id])],
        };

      case ConnectionTypes.patientToDoctor:
        return {
          ...updatedState,
          monitoredUsers: [...new Set([...state.monitoredUsers, user.id])],
        };

      case ConnectionTypes.addSubAdvisor:
        return {
          ...updatedState,
          subAdvisors: [...new Set([...state.subAdvisors, user.id])],
        };

      default:
        return updatedState;
    }
  }),

  on(DeveloperActions.removeUserConnectionSuccess, (state, { removedUserId, connectionType }) => {
    let updates = {};
    switch (connectionType) {
      case ConnectionTypes.doctorToPatient:
        updates = {
          monitoringUsers: state.monitoringUsers.filter((id) => id !== removedUserId),
        };
        break;
      case ConnectionTypes.patientToDoctor:
        updates = {
          monitoredUsers: state.monitoredUsers.filter((id) => id !== removedUserId),
        };
        break;
      case ConnectionTypes.addSubAdvisor:
        updates = {
          subAdvisors: state.subAdvisors.filter((id) => id !== removedUserId),
        };
        break;
    }
    return usersAdapter.removeOne(removedUserId, { ...state, ...updates });
  }),

  on(UserApiActions.setSelectedUserId, (state, { userId }) => ({
    ...state,
    selectedUserId: userId,
  })),
  on(UserApiActions.getUserDetailsSuccess, (state: UsersState, { userDetailsResponse }) => ({
    ...state,
    userDetails: userDetailsResponse,
  })),
  on(UserApiActions.removeMonitoredUsersSuccess, (state, { removedMonitoredUserIds }) => {
    return usersAdapter.removeMany(removedMonitoredUserIds, { ...state });
  }),
  on(UserApiActions.deleteUserSuccess, (state, { userId }) =>
    usersAdapter.removeOne(userId, state)
  ),
  on(updateUserSuccess, (state, { response }) => ({
    ...state,
    userResponse: response,
  })),
  on(UserApiActions.purgeUserDetails, (state) => ({
    ...state,
    userDetails: undefined,
  })),
  on(UserApiActions.confirmUserAccountSuccess, (state, { confirmed }) => ({
    ...state,
    userDetails: {
      ...state.userDetails,
      user: {
        ...state.userDetails!.user,
        isConfirmed: confirmed,
      },
    },
  })),
  on(UserApiActions.toggleUserApprovalSuccess, (state) => ({
    ...state,
    userDetails: {
      ...state.userDetails,
      user: {
        ...state.userDetails!.user,
        isApproved: !state.userDetails!.user.isApproved,
      },
    },
  })),

  on(UserApiActions.changeMonitoredUserFlowSuccess, (state, { changeMonitoredUserFlow }) => {
    return usersAdapter.updateOne(
      {
        id: changeMonitoredUserFlow.monitoredUserId,
        changes: {
          flow: changeMonitoredUserFlow.selectedFlow,
        },
      },
      {
        ...state,
      }
    );
  }),

  on(UserApiActions.submitMonitoredUserInvitation, (state) => ({
    ...state,
    pendingInvitationEmail: null,
  })),

  on(UserApiActions.submitMonitoredUserInvitationCheckNotFound, (state, { email }) => ({
    ...state,
    pendingInvitationEmail: email,
  })),

  on(ModalsActions.closeInviteMonitoredUserModal, (state) => ({
    ...state,
    pendingInvitationEmail: null,
  })),

  on(ModalsActions.closeUserDetailsModal, (state) => ({
    ...state,
    selectedUserId: '',
    userDetails: undefined,
  })),
);
